import React from 'react';
import { StandardTableActions, Badge } from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Payable } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';

const payableStatusVariant = (status?: string): BadgeVariant => {
  const s = status || 'pending';
  if (s === 'paid') return 'success';
  if (s === 'overdue') return 'danger';
  return 'warning';
};

export interface PayablesListContext {
  onMarkPaid?: (item: Payable) => void;
  onDuplicate?: (item: Payable) => Promise<void>;
}

export const moduleConfig = {
  module: {
    title: 'Payables',
    description: 'Bills and incoming invoices',
    permissionModuleKey: 'payables',
    route: {
      list: '/payables',
      new: '/payables/new',
      detail: (id: number) => `/payables/${id}`,
      edit: (id: number) => `/payables/${id}/edit`,
    },
    api: {
      list: '/payables',
      detail: (id: number) => `/payables/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search by payee or notes...',
  },
  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['status', 'payeeName', 'categoryId', 'invoiceDate', 'dueDate', 'amount'] },
  ],
  filters: {
    fields: [
      { key: 'status', label: 'Status', placeholder: 'All statuses', type: 'select' as const, getValue: (item: Payable) => item.status || 'pending', options: [{ value: 'pending', label: 'Pending' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }] },
      { key: 'payeeName', label: 'Payee', placeholder: 'Search payee...', type: 'text' as const, getValue: (item: Payable) => item.payeeName, options: [] },
      { key: 'categoryId', label: 'Category', placeholder: 'All categories', type: 'select' as const, getValue: (item: Payable) => item.categoryDescr?.name ?? (item.categoryId != null ? `#${item.categoryId}` : ''), options: [], apiEndpoint: '/crm-expense-category', labelField: 'name' },
      { key: 'invoiceDate', label: 'Invoice Date', placeholder: 'All dates', type: 'date' as const, getValue: (item: Payable) => (item.invoiceDate ? formatDateGlobal(item.invoiceDate) : undefined), options: [] },
      { key: 'dueDate', label: 'Due Date', placeholder: 'All due dates', type: 'date' as const, getValue: (item: Payable) => (item.dueDate ? formatDateGlobal(item.dueDate) : undefined), options: [] },
      { key: 'amount', label: 'Amount', placeholder: 'Amount', type: 'number' as const, getValue: (item: Payable) => item.amount?.toString(), options: [] },
    ],
    applyFilter: (
      items: Payable[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges ?? {};
      return items.filter((item) => {
        const statusArr = Array.isArray(filters.status) ? filters.status : filters.status ? [filters.status] : [];
        if (statusArr.length > 0 && statusArr.some((v) => (v || '').trim())) {
          const s = (item.status || 'pending').toLowerCase();
          if (!statusArr.some((f) => String(f).trim().toLowerCase() === s)) return false;
        }

        const payeeFilter = typeof filters.payeeName === 'string' ? filters.payeeName : Array.isArray(filters.payeeName) ? (filters.payeeName as string[])[0] : '';
        if (payeeFilter && String(payeeFilter).trim()) {
          if (!(item.payeeName || '').toLowerCase().includes(String(payeeFilter).trim().toLowerCase())) return false;
        }

        const catArr = Array.isArray(filters.categoryId) ? filters.categoryId : filters.categoryId ? [filters.categoryId] : [];
        if (catArr.length > 0 && catArr.some((v) => (v || '').trim())) {
          const displayName = item.categoryDescr?.name ?? (item.categoryId != null ? `#${item.categoryId}` : '');
          const match = catArr.some((f) => {
            const v = String(f).trim();
            return displayName === v || (item.categoryId != null && v === `#${item.categoryId}`);
          });
          if (!match) return false;
        }

        if (filters.invoiceDate && (Array.isArray(filters.invoiceDate) ? filters.invoiceDate : [filters.invoiceDate]).length > 0) {
          const range = ranges.invoiceDate;
          if (range?.start != null || range?.end != null) {
            const itemDate = item.invoiceDate ? new Date(item.invoiceDate) : null;
            if (!itemDate) return false;
            if (range.start != null && itemDate < range.start) return false;
            if (range.end != null && itemDate > range.end) return false;
          }
        }

        if (filters.dueDate && (Array.isArray(filters.dueDate) ? filters.dueDate : [filters.dueDate]).length > 0) {
          const range = ranges.dueDate;
          if (range?.start != null || range?.end != null) {
            const itemDate = item.dueDate ? new Date(item.dueDate) : null;
            if (!itemDate) return false;
            if (range.start != null && itemDate < range.start) return false;
            if (range.end != null && itemDate > range.end) return false;
          }
        }

        if (Array.isArray(filters.amount) && filters.amount.length > 0 && filters.amount.some((v) => (v || '').trim())) {
          const v = item.amount?.toString() || '';
          if (!filters.amount.some((f) => v === String(f).trim() || String(f).trim() === v)) return false;
        }

        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add Payable',
        showTitle: false,
        variant: 'primary' as const,
        href: '/payables/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Payable) => {
      window.location.href = `/payables/${item.id}`;
    },
    keyExtractor: (item: Payable) => item.id,
  },
  emptyStates: {
    noData: 'Add a bill or invoice to get started.',
    noSearchResults: 'No payables match your search.',
    noFilterResults: 'No payables match the selected filters.',
  },
  delete: {
    title: 'Delete Payable',
    message: 'Are you sure you want to delete this payable?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete payable.',
    handler: async (id: number) => {
      const { deletePayable } = await import('../../services/mutations');
      await deletePayable(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '-'),
    formatCurrency: (amount?: number) =>
      amount == null ? '-' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount),
  },
  detail: {
    subtitle: 'Payable Details',
    notFoundTitle: 'Payable not found',
    notFoundMessage: 'This payable does not exist.',
    notFoundBackText: 'Back to Payables',
    defaultTitle: 'Unnamed Payable',
    getTitle: (item: Payable) => item.payeeName || `Payable #${item.id}`,
  },
  form: {
    new: { title: 'New Payable', subtitle: 'Add a bill or invoice', submitText: 'Create Payable' },
    edit: { title: 'Edit Payable', subtitle: 'Update payable', submitText: 'Update Payable' },
    cancelText: 'Cancel',
    loadingText: 'Loading payable...',
    errorMessage: 'Failed to save payable.',
    supportsFiles: true,
    create: async (data: Partial<Payable> & { categoryId?: string | number; subCategoryId?: string | number; payeeId?: string | number; payeeName?: string }, files?: File[], filesToDetach?: number[]) => {
      const { createPayable } = await import('../../services/mutations');
      const payeeId = data.payeeId != null && String(data.payeeId) !== '' ? Number(data.payeeId) : undefined;
      const payload = {
        ...data,
        payeeId,
        payeeName: !payeeId && data.payeeName ? String(data.payeeName).trim() : undefined,
        categoryId: data.categoryId != null && String(data.categoryId) !== '' ? Number(data.categoryId) : null,
        subCategoryId: data.subCategoryId != null && String(data.subCategoryId) !== '' ? Number(data.subCategoryId) : null,
      };
      return await createPayable(payload, files, filesToDetach);
    },
    update: async (id: number, data: Partial<Payable> & { categoryId?: string | number; subCategoryId?: string | number; payeeId?: string | number; payeeName?: string }, files?: File[], filesToDetach?: number[]) => {
      const { updatePayable } = await import('../../services/mutations');
      const { id: _id, ...rest } = data as Record<string, unknown>;
      const payeeId = data.payeeId != null && String(data.payeeId) !== '' ? Number(data.payeeId) : undefined;
      const payload = {
        ...rest,
        payeeId,
        payeeName: !payeeId && data.payeeName ? String(data.payeeName).trim() : undefined,
        categoryId: data.categoryId != null && String(data.categoryId) !== '' ? Number(data.categoryId) : null,
        subCategoryId: data.subCategoryId != null && String(data.subCategoryId) !== '' ? Number(data.subCategoryId) : null,
        status: data.status,
        paymentDate: data.paymentDate,
      };
      return await updatePayable(id, payload, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) => `/payables/${entityId}/files/${fileId}`,
  },
  fields: [
      {
        name: 'payeeId',
      label: 'Payee',
      type: 'select',
      placeholder: 'Select payee (supplier, landlord, utility...)',
      options: [{ value: '', label: 'Select...' }],
      apiEndpoint: '/payees',
      labelField: 'name',
      valueField: 'id' as const,
      validation: {
        requiredWhen: (formData: Record<string, unknown>) =>
          !formData.payeeId && !(typeof formData.payeeName === 'string' && formData.payeeName.trim()),
        message: 'Select a payee or keep the extracted payee name',
      },
      onChangeSideEffect: async (value, _setFieldValue, setMultipleFieldValues) => {
        if (value == null || value === '') return;
        const api = (await import('../../services/api')).default;
        const res = await api.get(`/payees/${value}`);
        const payee = res.data as { name?: string; defaultCategoryId?: number; defaultSubcategoryId?: number };
        const updates: Record<string, string> = {};
        if (payee?.name) updates.payeeName = payee.name;
        if (payee?.defaultCategoryId != null) {
          updates.categoryId = String(payee.defaultCategoryId);
        }
        if (Object.keys(updates).length) setMultipleFieldValues(updates);
        if (payee?.defaultSubcategoryId != null) {
          setTimeout(() => {
            setMultipleFieldValues({ subCategoryId: String(payee.defaultSubcategoryId) });
          }, 800);
        }
      },
    },
    {
      name: 'payeeName',
      label: 'Payee name (if not in list)',
      type: 'text',
      placeholder: 'Used when no payee is selected',
    },
    { name: 'invoiceDate', label: 'Invoice Date', type: 'date', placeholder: 'Invoice date' },
    { name: 'dueDate', label: 'Due Date', type: 'date', placeholder: 'Due date' },
    { name: 'amount', label: 'Amount', type: 'number', placeholder: 'Amount', validation: { required: true, min: 0 } },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Status',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
      ],
      onChangeSideEffect: (value, setFieldValue) => {
        if (value === 'pending') setFieldValue('paymentDate', undefined);
      },
    },
    {
      name: 'paymentDate',
      label: 'Payment Date',
      type: 'date',
      placeholder: 'Required when status is Paid',
      validation: {
        requiredWhen: (formData: Record<string, unknown>) => formData.status === 'paid',
        message: 'Payment date is required when status is Paid',
      },
    },
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      placeholder: 'Category (optional)',
      options: [{ value: '', label: 'None' }],
      apiEndpoint: '/crm-expense-category',
      labelField: 'name',
      valueField: 'id' as const,
    },
    {
      name: 'subCategoryId',
      label: 'Subcategory',
      type: 'select',
      placeholder: 'Select category first',
      options: [{ value: '', label: 'None' }],
      optionsEndpointFromFormData: (formData) => {
        const id = formData.categoryId;
        if (id == null || id === '') return null;
        return `/payees/subcategories/${Number(id)}`;
      },
      optionsDependOnFields: ['categoryId'],
      labelField: 'name',
      valueField: 'id' as const,
    },
    { name: 'notes', label: 'Description', type: 'text', placeholder: 'Description' },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Payable Information',
        icon: '📄',
        gridCols: 'md:grid-cols-12',
        fields: ['payeeId', 'payeeName', 'invoiceDate', 'dueDate', 'amount', 'notes', 'categoryId', 'subCategoryId', 'status', 'paymentDate'],
        fieldSpans: {
          payeeId: 'md:col-span-6',
          payeeName: 'md:col-span-6',
          invoiceDate: 'md:col-span-4',
          dueDate: 'md:col-span-4',
          amount: 'md:col-span-4',
          notes: 'md:col-span-12',
          categoryId: 'md:col-span-6',
          subCategoryId: 'md:col-span-6',
          status: 'md:col-span-6',
          paymentDate: 'md:col-span-6',
        },
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (
  formatDate: (dateString?: string) => string,
  formatCurrency?: (amount?: number) => string,
  colors?: ThemeColors
): Column<Payable>[] => {
  const fmt = formatCurrency || moduleConfig.utils.formatCurrency;
  const text = colors?.text || 'text-gray-900';
  const secondary = colors?.textSecondary || 'text-gray-700';
  return [
    {
      key: 'payeeName',
      header: 'Payee',
      sortable: true,
      width: '200px',
      render: (_, row) => (
        <div className={`text-sm ${text}`}>{row.payeeName || '-'}</div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      width: '350px',
      render: (_, row) => (
        <div className="min-w-0">
          <div className={`text-sm ${text}`}>{row.categoryDescr?.name || '-'}</div>
          {row.subCategoryDescr?.name && (
            <div className={`text-xs ${secondary} truncate flex items-center gap-1`}>
              <svg className="w-3.5 h-3.5 shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {row.subCategoryDescr.name}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'invoiceDate',
      header: 'Dates',
      sortable: true,
      render: (_, row) => (
        <div className="min-w-0">
          <div className={`text-sm ${text}`}>{formatDate(row.invoiceDate)}</div>
          <div className={`text-xs ${secondary}`}>Due: {formatDate(row.dueDate)}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (_, row) => (
        <div className="min-w-0">
          <div className={`text-sm ${text} font-semibold`}>{fmt(row.amount)}</div>
          {row.paymentDate && (
            <div className={`text-xs ${secondary}`}>Paid: {formatDate(row.paymentDate)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_, row) => {
        const raw = row.status || 'pending';
        const isOverdue =
          raw === 'pending' &&
          row.dueDate != null &&
          new Date(row.dueDate).getTime() < Date.now();
        const s = isOverdue ? 'overdue' : raw;
        return <Badge variant={payableStatusVariant(s)}>{s}</Badge>;
      },
    },
  ];
};

export const getDetailSections = (
  item: Payable,
  formatDate: (dateString?: string) => string,
  formatCurrency: (amount?: number) => string
) => {
  const sections: Array<{ title: string; icon: string; fields: Array<{ label: string; value: React.ReactNode }> }> = [
    {
      title: 'Payable Information',
      icon: '📄',
      fields: [
        { label: 'Payee', value: item.payeeName },
        { label: 'Invoice Date', value: formatDate(item.invoiceDate) },
        { label: 'Due Date', value: formatDate(item.dueDate) },
        { label: 'Amount', value: formatCurrency(item.amount) },
        { label: 'Status', value: item.status || 'pending' },
        { label: 'Payment Date', value: formatDate(item.paymentDate) },
        { label: 'Category', value: item.categoryDescr?.name || '-' },
        { label: 'Subcategory', value: item.subCategoryDescr?.name || '-' },
        { label: 'Notes', value: item.notes || '-' },
      ],
    },
    {
      title: 'Additional',
      icon: '📋',
      fields: [
        { label: 'Created By', value: item.createdBy?.userFirstName ?? '-' },
        { label: 'Updated By', value: item.updatedBy?.userFirstName ?? '-' },
        { label: 'Created At', value: formatDate(item.createdAt) },
        { label: 'Updated At', value: formatDate(item.updatedAt) },
      ],
    },
  ];
  sections.push({
    title: 'Attachments',
    icon: '📎',
    fields: [
      {
        label: '',
        value: React.createElement(EntityAttachmentsSection, {
          downloadEndpoint: `/payables/${item.id}/files`,
          files: item.FileAttachment || [],
          defaultDownloadName: `payable_${item.id}_files.zip`,
          getViewFileUrl: (fileId) => `/payables/${item.id}/files/${fileId}`,
        }),
      },
    ],
  });
  return sections;
};

export const getStatusBadges = (item: Payable): React.ReactNode => {
  const s = item.status || 'pending';
  return <Badge variant={payableStatusVariant(s)}>{s}</Badge>;
};

export type PayableFormData = Partial<Omit<Payable, 'categoryId' | 'subCategoryId' | 'payeeId'>> & {
  categoryId?: string;
  subCategoryId?: string;
  payeeId?: string;
  payeeName?: string;
};

export const getInitialFormData = (): PayableFormData => ({
  payeeId: '',
  payeeName: '',
  invoiceDate: undefined,
  dueDate: undefined,
  amount: undefined,
  notes: '',
  categoryId: '',
  subCategoryId: '',
  status: 'pending',
  paymentDate: undefined,
});

export const mapItemToFormData = (item: Payable): PayableFormData => ({
  id: item.id,
  payeeId: item.payeeId != null ? String(item.payeeId) : '',
  payeeName: item.payeeName || '',
  invoiceDate: item.invoiceDate,
  dueDate: item.dueDate,
  amount: item.amount,
  notes: item.notes || '',
  categoryId: item.categoryId != null ? String(item.categoryId) : '',
  subCategoryId: item.subCategoryId != null ? String(item.subCategoryId) : '',
  status: (item.status as 'pending' | 'paid') || 'pending',
  paymentDate: item.paymentDate,
});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add')
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  return null;
};

export const getTableActions = (
  item: Payable,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

